import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, PlusCircle, Coins, ArrowUp, ArrowDown, Eye, Download, TrendingUp, Users as UsersIcon, DollarSign, X, Package, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { VirtualCurrencyService, CoinTransaction, UserCoins, CoinAnalytics } from '../services/VirtualCurrencyService';
import { supabase } from '../lib/supabase';
import { useUser } from '../context/UserContext';
import { AdminCoinPackages } from '../components/AdminCoinPackages';
import { AdminSubscriptionPlans } from '../components/AdminSubscriptionPlans';

interface UserWithCoins extends UserCoins {
  username?: string;
  full_name?: string;
  user_created_at?: string;
}

interface TransactionWithUser extends CoinTransaction {
  username?: string;
}

export const AdminCoins: React.FC = () => {
  const { user } = useUser();
  const coinService = new VirtualCurrencyService();

  const [userCoins, setUserCoins] = useState<UserWithCoins[]>([]);
  const [transactions, setTransactions] = useState<TransactionWithUser[]>([]);
  const [analytics, setAnalytics] = useState<CoinAnalytics | null>(null);
  const [loading, setLoading] = useState({
    users: true,
    transactions: true,
    analytics: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'users' | 'transactions' | 'packages' | 'subscriptions'>('users');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [transactionAmount, setTransactionAmount] = useState(0);
  const [transactionType, setTransactionType] = useState<'add' | 'deduct'>('add');
  const [transactionDesc, setTransactionDesc] = useState('');
  const [giftType, setGiftType] = useState<'gift' | 'reload' | 'promotion' | 'compensation'>('gift');
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchResults, setSearchResults] = useState<UserWithCoins[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  useEffect(() => {
    loadAnalytics();
    loadUsers();
    loadTransactions();
  }, []);

  const loadAnalytics = async () => {
    try {
      const data = await coinService.getGlobalCoinStats();
      setAnalytics(data);
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(prev => ({ ...prev, analytics: false }));
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(prev => ({ ...prev, users: true }));
      const { data: coinsData } = await coinService.getAllUserCoins(100, 0);

      const userIds = coinsData.map(c => c.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, full_name, created_at')
        .in('id', userIds);

      const usersWithProfiles = coinsData.map(coins => ({
        ...coins,
        username: profiles?.find(p => p.id === coins.user_id)?.username,
        full_name: profiles?.find(p => p.id === coins.user_id)?.full_name,
        user_created_at: profiles?.find(p => p.id === coins.user_id)?.created_at
      }));

      setUserCoins(usersWithProfiles);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load user balances');
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(prev => ({ ...prev, transactions: true }));
      const { data: txData } = await coinService.getAllTransactions({
        limit: 100,
        offset: 0
      });

      const userIds = [...new Set(txData.map(t => t.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', userIds);

      const txWithUsers = txData.map(tx => ({
        ...tx,
        username: profiles?.find(p => p.id === tx.user_id)?.username
      }));

      setTransactions(txWithUsers);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(prev => ({ ...prev, transactions: false }));
    }
  };

  const filteredUsers = userCoins
    .filter(u => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        u.user_id.toLowerCase().includes(search) ||
        u.username?.toLowerCase().includes(search) ||
        u.full_name?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      if (!a.user_created_at) return 1;
      if (!b.user_created_at) return -1;
      return new Date(b.user_created_at).getTime() - new Date(a.user_created_at).getTime();
    });

  const filteredTransactions = transactions.filter(t => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      t.user_id.toLowerCase().includes(search) ||
      t.username?.toLowerCase().includes(search) ||
      t.transaction_type.toLowerCase().includes(search) ||
      t.description?.toLowerCase().includes(search)
    );
  });

  const searchUsers = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 1) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchingUsers(true);

      const words = searchTerm.trim().split(/\s+/);

      let query = supabase
        .from('profiles')
        .select('id, username, full_name');

      if (words.length === 1) {
        query = query.or(`username.ilike.%${words[0]}%,full_name.ilike.%${words[0]}%`);
      } else {
        const conditions = words.map(word =>
          `username.ilike.%${word}%,full_name.ilike.%${word}%`
        ).join(',');
        query = query.or(conditions);
      }

      const { data: profiles } = await query.limit(50);

      if (profiles) {
        const userIds = profiles.map(p => p.id);
        const { data: coinsData } = await supabase
          .from('user_coins')
          .select('*')
          .in('user_id', userIds);

        const results = profiles.map(profile => {
          const coins = coinsData?.find(c => c.user_id === profile.id);
          return {
            id: coins?.id || '',
            user_id: profile.id,
            balance: coins?.balance || 0,
            created_at: coins?.created_at || '',
            updated_at: coins?.updated_at || null,
            username: profile.username,
            full_name: profile.full_name
          };
        });

        const searchLower = searchTerm.toLowerCase();
        const scored = results.map(r => {
          let score = 0;
          const nameLower = (r.full_name || '').toLowerCase();
          const usernameLower = (r.username || '').toLowerCase();

          if (usernameLower.includes(searchLower) || nameLower.includes(searchLower)) {
            score += 10;
          }

          words.forEach(word => {
            const wordLower = word.toLowerCase();
            if (usernameLower.includes(wordLower)) score += 5;
            if (nameLower.includes(wordLower)) score += 3;
            if (usernameLower.startsWith(wordLower)) score += 3;
            if (nameLower.startsWith(wordLower)) score += 2;
          });

          return { ...r, score };
        });

        scored.sort((a, b) => b.score - a.score);
        setSearchResults(scored.slice(0, 20));
      }
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!selectedUser || transactionAmount <= 0 || !user) return;

    try {
      if (transactionType === 'add') {
        await coinService.giftCoins(
          selectedUser,
          transactionAmount,
          user.id,
          giftType,
          transactionDesc || undefined
        );
        setSuccess(`Successfully gifted ${transactionAmount} coins!`);
      } else {
        await coinService.adminDeductCoins(
          selectedUser,
          transactionAmount,
          user.id,
          transactionDesc || 'Admin deduction'
        );
        setSuccess(`Successfully deducted ${transactionAmount} coins!`);
      }

      await loadUsers();
      await loadTransactions();
      await loadAnalytics();

      setShowTransactionModal(false);
      setTransactionAmount(0);
      setTransactionDesc('');
      setSelectedUser(null);
      setUserSearchTerm('');
      setSearchResults([]);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error processing transaction:', err);
      setError(err instanceof Error ? err.message : 'Failed to process transaction');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleExportCSV = async () => {
    try {
      const csv = await coinService.exportTransactionsToCSV();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coin-transactions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess('CSV exported successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV');
      setTimeout(() => setError(null), 5000);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/admin/dashboard" className="mr-4 text-white/70 hover:text-white transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl font-bold">Virtual Currency Management</h1>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-500">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 text-green-500">
          {success}
        </div>
      )}

      {/* Analytics Cards */}
      {!loading.analytics && analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-sm">Total in Circulation</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {Math.round(analytics.total_coins_in_circulation).toLocaleString()}
                </p>
              </div>
              <Coins className="text-yellow-400" size={32} />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-sm">Users with Coins</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {analytics.users_with_coins}
                </p>
              </div>
              <UsersIcon className="text-blue-400" size={32} />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-sm">Total Consumed</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {Math.abs(Math.round(analytics.total_coins_consumed)).toLocaleString()}
                </p>
              </div>
              <TrendingUp className="text-red-400" size={32} />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-sm">Total Gifted</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {Math.round(analytics.total_coins_gifted).toLocaleString()}
                </p>
              </div>
              <DollarSign className="text-green-400" size={32} />
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="flex space-x-4">
          <button
            className={`px-4 py-2 rounded-lg ${viewMode === 'users' ? 'bg-kod-accent text-white' : 'bg-kod-purple-700 text-white/70'}`}
            onClick={() => setViewMode('users')}
          >
            User Balances
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${viewMode === 'transactions' ? 'bg-kod-accent text-white' : 'bg-kod-purple-700 text-white/70'}`}
            onClick={() => setViewMode('transactions')}
          >
            Transactions
          </button>
          <button
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${viewMode === 'packages' ? 'bg-kod-accent text-white' : 'bg-kod-purple-700 text-white/70'}`}
            onClick={() => setViewMode('packages')}
          >
            <Package size={16} />
            Packages
          </button>
          <button
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${viewMode === 'subscriptions' ? 'bg-teal-600 text-white' : 'bg-kod-purple-700 text-white/70'}`}
            onClick={() => setViewMode('subscriptions')}
          >
            <RefreshCw size={16} />
            Subscriptions
          </button>
        </div>

        <div className="flex space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" size={18} />
            <input
              type="text"
              placeholder={`Search ${viewMode === 'users' ? 'users' : 'transactions'}...`}
              className="pl-10 pr-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent text-white w-60"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {viewMode === 'users' && (
            <button
              className="bg-kod-accent hover:bg-kod-accent-dark text-white px-4 py-2 rounded-lg flex items-center"
              onClick={() => {
                setSelectedUser(null);
                setTransactionAmount(0);
                setTransactionDesc('');
                setTransactionType('add');
                setUserSearchTerm('');
                setSearchResults([]);
                setShowTransactionModal(true);
              }}
            >
              <PlusCircle size={18} className="mr-2" />
              Gift Coins
            </button>
          )}

          {viewMode === 'transactions' && (
            <button
              className="bg-kod-purple-700 hover:bg-kod-purple-600 text-white px-4 py-2 rounded-lg flex items-center"
              onClick={handleExportCSV}
            >
              <Download size={18} className="mr-2" />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {viewMode === 'subscriptions' ? (
        <div className="card">
          <AdminSubscriptionPlans />
        </div>
      ) : viewMode === 'packages' ? (
        <div className="card">
          <AdminCoinPackages />
        </div>
      ) : viewMode === 'users' ? (
        <div className="card">
          {loading.users ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-kod-accent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-kod-purple-600">
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Full Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Balance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Created At (User)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Last Updated</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kod-purple-600/50">
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-kod-purple-700/50">
                        <td className="px-4 py-4 whitespace-nowrap">{u.username || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap">{u.full_name || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Coins size={16} className="text-yellow-400 mr-2" />
                            {Math.round(u.balance).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {u.user_created_at ? new Date(u.user_created_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {u.updated_at ? new Date(u.updated_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              className="p-1 text-white/70 hover:text-green-400"
                              onClick={() => {
                                setSelectedUser(u.user_id);
                                setTransactionType('add');
                                setShowTransactionModal(true);
                              }}
                              title="Add coins"
                            >
                              <ArrowUp size={18} />
                            </button>
                            <button
                              className="p-1 text-white/70 hover:text-red-400"
                              onClick={() => {
                                setSelectedUser(u.user_id);
                                setTransactionType('deduct');
                                setShowTransactionModal(true);
                              }}
                              title="Deduct coins"
                            >
                              <ArrowDown size={18} />
                            </button>
                            <button
                              className="p-1 text-white/70 hover:text-white"
                              onClick={() => {
                                setViewMode('transactions');
                                setSearchTerm(u.username || '');
                              }}
                              title="View transactions"
                            >
                              <Eye size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-white/50">
                        No users found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          {loading.transactions ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-kod-accent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-kod-purple-600">
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kod-purple-600/50">
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-kod-purple-700/50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {new Date(tx.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {tx.username || tx.user_id.slice(0, 8) + '...'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap capitalize">
                          <span className={`px-2 py-1 rounded text-xs ${
                            tx.transaction_type === 'admin_gift' ? 'bg-green-500/20 text-green-400' :
                            tx.transaction_type === 'admin_deduction' ? 'bg-red-500/20 text-red-400' :
                            tx.transaction_type === 'performance_scoring' ? 'bg-purple-500/20 text-purple-400' :
                            tx.transaction_type === 'tournament_entry' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {tx.transaction_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap font-semibold ${
                          parseFloat(tx.amount.toString()) > 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {parseFloat(tx.amount.toString()) > 0 ? '+' : ''}{parseFloat(tx.amount.toString()).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 text-sm text-white/70">{tx.description || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-white/50">
                        No transactions found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showTransactionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-6">
              {transactionType === 'add' ? 'Gift Coins' : 'Deduct Coins'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">User</label>
                {selectedUser ? (
                  <div className="flex items-center justify-between px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-md">
                    <span className="text-white">
                      {(() => {
                        const foundUser = userCoins.find(u => u.user_id === selectedUser) ||
                                        searchResults.find(u => u.user_id === selectedUser);
                        return foundUser?.full_name || foundUser?.username || selectedUser.slice(0, 8) + '...';
                      })()}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedUser(null);
                        setUserSearchTerm('');
                        setSearchResults([]);
                      }}
                      className="text-white/70 hover:text-white"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent"
                      placeholder="Search by username or name..."
                      value={userSearchTerm}
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value);
                        searchUsers(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      onBlur={() => {
                        setTimeout(() => setShowUserDropdown(false), 200);
                      }}
                    />
                    {searchingUsers && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-kod-accent"></div>
                      </div>
                    )}
                    {showUserDropdown && searchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-kod-purple-700 border border-kod-purple-600 rounded-md max-h-60 overflow-y-auto shadow-lg">
                        {searchResults.map(u => (
                          <button
                            key={u.user_id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-kod-purple-600 transition-colors text-white"
                            onClick={() => {
                              setSelectedUser(u.user_id);
                              setUserSearchTerm('');
                              setShowUserDropdown(false);
                              setSearchResults([]);
                            }}
                          >
                            <div className="font-medium">{u.full_name || u.username || u.user_id.slice(0, 8) + '...'}</div>
                            {u.full_name && u.username && (
                              <div className="text-xs text-white/60">@{u.username}</div>
                            )}
                            <div className="text-xs text-white/60 flex items-center mt-1">
                              <Coins size={12} className="mr-1 text-yellow-400" />
                              {Math.round(u.balance).toLocaleString()} coins
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showUserDropdown && userSearchTerm.length >= 1 && searchResults.length === 0 && !searchingUsers && (
                      <div className="absolute z-50 w-full mt-1 bg-kod-purple-700 border border-kod-purple-600 rounded-md p-3 text-center text-white/50 text-sm shadow-lg">
                        No users found
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Amount</label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  value={transactionAmount}
                  onChange={(e) => setTransactionAmount(parseInt(e.target.value) || 0)}
                />
              </div>

              {transactionType === 'add' && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Gift Type</label>
                  <select
                    className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent"
                    value={giftType}
                    onChange={(e) => setGiftType(e.target.value as typeof giftType)}
                  >
                    <option value="gift">Gift</option>
                    <option value="reload">Reload</option>
                    <option value="promotion">Promotion</option>
                    <option value="compensation">Compensation</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {transactionType === 'add' ? 'Note' : 'Reason'}
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  value={transactionDesc}
                  onChange={(e) => setTransactionDesc(e.target.value)}
                  placeholder={transactionType === 'add' ? 'Optional note' : 'Reason for deduction'}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-md transition-colors"
                onClick={() => {
                  setShowTransactionModal(false);
                  setSelectedUser(null);
                  setTransactionAmount(0);
                  setTransactionDesc('');
                  setUserSearchTerm('');
                  setSearchResults([]);
                }}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 text-white rounded-md transition-colors ${
                  !selectedUser || transactionAmount <= 0
                    ? 'bg-kod-accent/50 cursor-not-allowed'
                    : 'bg-kod-accent hover:bg-kod-accent-dark'
                }`}
                onClick={handleAddTransaction}
                disabled={!selectedUser || transactionAmount <= 0}
              >
                {transactionType === 'add' ? 'Gift Coins' : 'Deduct Coins'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
